import { create } from "zustand";
import {
  RepoNode,
  Project,
  SelectedFile,
  SelectedElement,
  CodeChange,
  SelectedCode,
  Branch,
  PageEdit,
  ElementEdit,
  MediaItem,
  UIChatMessage,
  StreamStatus,
} from "@/types/types";
import { EditorView } from "@uiw/react-codemirror";

interface EditorState {
  // userId: string;
  // userName: string;
  // userEmail: string;
  // accessToken: string | null;
  // login: string | null;

  gitAPIAuthenticated: boolean;
  sidebarOption: string;
  chatMessages: UIChatMessage[];
  topPanelSize: number;
  bottomPanelSize: number;
  sidebarPanelSize: number;
  editorPanelSize: number;
  codePanelSize: number;
  iframePanelSize: number;
  previewPanelSize: number;
  aiHubPanelSize: number;
  action: string;
  device: string;
  editMode: string;
  previewTheme: string | 'light' | 'dark';
  title: string;
  state: string;
  tree: RepoNode[];
  selectedFile: SelectedFile | null;
  selectedElement: SelectedElement | null;
  selectedCode: SelectedCode | null;
  selectedFileContent: string;
  srcDoc: string | null;

  projectId: string | null;
  project: Project | null;
  repoId: string | null;
  lastGitCommitSha: string | null;
  // fileType: string;
  codeChange: CodeChange | null;
  importedStyles: SelectedFile[] | null;
  importedScripts: SelectedFile[] | null;

  // CodeMirror specific state
  editorView: EditorView | null;
  codeView: string;
  changesCount: number;
  hoveredElement: any; // Adjust type as needed
  changesList: CodeChange[];
  currentChangeIndex: number;
  isChatMode: boolean;
  hasUndo: boolean;
  hasRedo: boolean;
  // UI state
  isIframeReady: boolean;
  isCodeEditorReady: boolean;
  selector: boolean;
  codeViewer: string;
  scopeView: string;
  scopeStyles: SelectedFile[];
  scopeScripts: SelectedFile[];
  repoName: string;
  selectedBranch: Branch;
  branches: Branch[];
  repoOwner: string;
  updateGit: boolean;
  forceIframeRefresh: boolean;

  // AI Designer
  companyName: string;
  companyDescription: string;
  location: string;

  projectSections: any[];
  projectLayout: any;
  projectLayoutStyle: string;
  projectUIFramework: string;
  projectStyles: any[];
  projectTexts: any[];
  // projectImages: any[];
  // projectAnimations: any[];
  projectPrimaryColor: string;
  projectSecondaryColor: string;
  projectOtherDetails: string;
  projectAudience: string;
  projectVision: string;
  projectMission: string;
  projectProducts: string[];
  enabledEditor: any;
  projectActiveStep: string;
  // generatedContent: any;
  projectIndustry: string;
  projectGoal: string;
  projectPageName: string;
  projectImagesLinks: string[];
  projectVideosLinks: string[];

  projectLoading: boolean;
  projectTextsLength: string;
  projectTone: string;
  projectAddNewSection: boolean;
  projectWebsiteCode: string;
  projectTypography: string;
  projectLanguage: string;
  newBranchCreated: boolean;

  isAIWorking: boolean;

  // New keys for model selection
  selectedModel: string;
  showChatPanel: boolean;
  newElementOuterHTML: string;
  replaceCalled: boolean;
  elementSnapshot: string | null;
  pageSnapshot: string | null;
  undoStack: (ElementEdit | PageEdit)[];
  redoStack: (ElementEdit | PageEdit)[];
  undonePageSnapshot: boolean;
  undoneElementSnapshot: boolean;
  zoom: number;
  apiCalled: boolean;
  isDemo: boolean;
  viewingMode: string;
  enableScaling: boolean;
  moveElements: boolean;
  chatVisible: boolean;
  chatExpanded: boolean;
  mediaItems: MediaItem[];
  streamStatus: StreamStatus;

  // Element edit streaming state
  elementEditStatus: 'idle' | 'streaming' | 'complete' | 'error';
  elementEditStreamHtml: string;
  elementEditOriginalHtml: string;

  // Actions
  setGitAPIAuthenticated: (authenticated: boolean) => void;
  setSidebarOption: (option: string) => void;
  setAction: (action: string) => void;
  setEnabledEditor: (id: string, enabled: boolean) => void;

  setDevice: (device: string) => void;
  setPreviewTheme: (previewTheme: string) => void;
  setTitle: (title: string) => void;
  setEditMode: (editMode: string) => void;
  setState: (state: string) => void;
  setTree: (tree: RepoNode[]) => void;
  setChatMessages: (messages: UIChatMessage[]) => void;
  setSelectedFile: (file: SelectedFile) => void;
  setSelectedElement: (elem: SelectedElement | null) => void;
  setNewElementOuterHTML: (outerHTML: string) => void;

  setSelectedCode: (elem: SelectedCode | null) => void;

  setSelectedFileContent: (content: string) => void;
  setSrcDoc: (doc: string | null) => void;

  setProjectId: (id: string | null) => void;
  setProject: (project: Project) => void;
  setRepoId: (id: string | null) => void;
  setLastGitCommitSha: (sha: string | null) => void;
  // setFileType: (fileType: string) => void;

  setSelector: (selector: boolean) => void;
  setCodeViewer: (viewer: string) => void;
  setScopeView: (view: string) => void;
  setScopeStyles: (styles: SelectedFile[]) => void;
  setScopeScripts: (scripts: SelectedFile[]) => void;
  setImportedStyles: (styles: SelectedFile[]) => void;
  setImportedScripts: (scripts: SelectedFile[]) => void;
  setRepoName: (name: string) => void;
  setSelectedBranch: (branch: Branch) => void;
  setBranches: (list: { name: string; sha: string }[]) => void;
  setRepoOwner: (owner: string) => void;
  setTopPanelSize: (size: number) => void;
  setBottomPanelSize: (size: number) => void;
  setSidebarPanelSize: (size: number) => void;
  setEditorPanelSize: (size: number) => void;
  setCodePanelSize: (size: number) => void;
  setIframePanelSize: (size: number) => void;
  setPreviewPanelSize: (size: number) => void;
  setAIHubPanelSize: (size: number) => void;
  setCodeChange: (change: CodeChange) => void;
  setUpdateGit: (update: boolean) => void;
  setNewBranchCreated: (created: boolean) => void;

  // CodeMirror actions
  setEditorView: (view: EditorView | null) => void;
  setCodeView: (newView: string) => void;
  setChangesCount: (count: number) => void;
  setHoveredElement: (element: any) => void; // Adjust type as needed
  setChangesList: (list: CodeChange[]) => void;
  setCurrentChangeIndex: (index: number) => void;
  addChange: (newChange: CodeChange) => void;
  undoChange: () => void;
  redoChange: () => void;
  saveChanges: () => void;
  setForceIframeRefresh: (refresh: boolean) => void;
  setIsCodeEditorReady: (isReady: boolean) => void;
  setIsIframeReady: (isReady: boolean) => void;

  // AI Designer
  setCompanyName: (companyName: string) => void;

  setCompanyDescription: (companyName: string) => void;
  setLocation: (location: string) => void;
  setProjectSections: (projectSections: any[]) => void;
  setProjectLayout: (projectLayout: any) => void;
  setProjectLayoutStyle: (projectLayoutStyle: string) => void;
  setProjectUIFramework: (projectUIFramework: string) => void;
  setProjectStyles: (projectStyles: any[]) => void;
  setProjectTexts: (projectTexts: any[]) => void;
  setProjectImagesLinks: (projectImagesLinks: any[]) => void;
  setProjectVideosLinks: (projectVideosLinks: any[]) => void;
  setProjectPrimaryColor: (color: string) => void;
  setProjectSecondaryColor: (color: string) => void;
  setProjectActiveStep: (step: string) => void;
  setProjectIndustry: (projectIndustry: string) => void;
  setProjectOtherDetails: (projectOtherDetails: string) => void;
  setProjectAudience: (projectAudience: string) => void;
  setProjectVision: (projectVision: string) => void;
  setProjectMission: (projectMission: string) => void;
  setProjectProducts: (projectProducts: string[]) => void;
  setProjectGoal: (projectGoal: string) => void;
  setProjectPageName: (name: string) => void;
  setProjectLoading: (projectLoading: boolean) => void;
  setProjectTextsLength: (projectTextsLength: string) => void;
  setProjectTone: (projectTone: string) => void;
  setProjectAddNewSection: (projectAddNewSection: boolean) => void;
  setProjectWebsiteCode: (projectWebsiteCode: string) => void;
  setProjectTypography: (projectTypography: string) => void;
  setProjectLanguage: (projectLanguage: string) => void;
  setIsAIWorking: (isAIWorking: boolean) => void;

  // New setter for the AI model selection
  setChatMode: (chat: boolean) => void;
  setHasUndo: (hasUndo: boolean) => void;
  setHasRedo: (hasRedo: boolean) => void;
  setSelectedModel: (model: string) => void;
  setShowChatPanel: (show: boolean) => void;
  setReplaceCalled: (replaced: boolean) => void;

  // Element edit streaming actions
  setElementEditStatus: (status: 'idle' | 'streaming' | 'complete' | 'error') => void;
  appendElementEditStreamHtml: (token: string) => void;
  setElementEditOriginalHtml: (html: string) => void;
  resetElementEditStream: () => void;

  addElementSnapshot: (value: string | null, messageId: string | null) => void;
  addPageSnapshot: (value: string | null, messageId: string | null) => void;
  // removeElementSnapshot: () => void;
  // removePageSnapshot: () => void;
  // clearSnapshots: () => void;

  // setUndonePageSnapshot: (undone: boolean) => void;
  // setUndoneElementSnapshot: (undone: boolean) => void;

  setZoom: (zoom: number) => void;
  setAPICalled: (called: boolean) => void;
  setIsDemo: (isDemo: boolean) => void;
  setViewingMode: (mode: string) => void;
  setEnableScaling: (enable: boolean) => void;
  setMoveElements: (move: boolean) => void;
  setChatVisible: (chatVisible: boolean) => void;
  setChatExpanded: (chatExpanded: boolean) => void;
  addMediaItem: (item: MediaItem) => void;
  removeMediaItem: (id: string) => void;
  setMediaItems: (items: MediaItem[]) => void; // ✅ Add this
  setStreamStatus: (status: StreamStatus) => void;
  resetStreamStatus: () => void;
  // ← NEW ACTION:
  // clearStore: () => void;
  // Batches all repo-related fields in one action
}
const useEditorStore = create<EditorState>((set) => ({
  // Initial state
  enabledEditor: {},
  gitAPIAuthenticated: false,
  sidebarOption: "CLOSE",
  topPanelSize: 60,
  bottomPanelSize: 40,
  sidebarPanelSize: 0,
  editorPanelSize: 30,
  codePanelSize: 0,
  iframePanelSize: 100,
  previewPanelSize: 70,
  aiHubPanelSize: 10,
  action: "",
  device: "desktop",
  editMode: "visual",
  previewTheme: "light",
  title: "Web Editor",
  state: "save",
  tree: [],
  chatMessages: [],
  selectedFile: null,
  selectedElement: null,
  newElementOuterHTML: "",
  selectedCode: null,
  selectedFileContent: "",
  srcDoc: null,
  projectId: null,
  repoId: null,
  lastGitCommitSha: null,
  fileType: "unknown",
  isComponentReady: false,
  selector: true,
  codeViewer: "html",
  scopeView: "",
  scopeStyles: [],
  scopeScripts: [],
  repoName: "",
  selectedBranch: { name: "main", sha: "" },
  branches: [],
  repoOwner: "",
  newBranchCreated: false,

  // previewSize: 50,

  // CodeMirror specific state
  editorView: null,
  codeView: "",
  changesCount: 0,
  hoveredElement: null,
  changesList: [],
  currentChangeIndex: -1,
  codeChange: null,
  forceIframeRefresh: false,
  isCodeEditorReady: false,
  isIframeReady: false,
  importedStyles: null,
  importedScripts: null,

  project: null,
  // AI Designer
  projectSections: [],
  projectLayout: null,
  projectLayoutStyle: "",
  projectUIFramework: "",
  projectStyles: [],
  projectTexts: [],
  projectImages: [3],
  projectAnimations: [3],
  projectPrimaryColor: "#000000",
  projectSecondaryColor: "#ffffff",
  projectActiveStep: "project",
  projectOtherDetails: "",
  projectAudience: "",
  projectVision: "",
  projectMission: "",
  projectProducts: [],
  projectImagesLinks: [],
  projectVideosLinks: [],

  companyName: "",
  companyDescription: "",
  location: "",
  projectIndustry: "",
  projectGoal: "",
  projectPageName: "",
  projectLoading: false,
  projectTextsLength: "",
  projectTone: "",
  projectAddNewSection: false,
  projectWebsiteCode: "",
  projectTypography: "",
  projectLanguage: "",
  isAIWorking: false,
  isChatMode: false,
  hasUndo: false,
  hasRedo: false,
  // New state for selected model – defaulting to "gpt-4o-mini"
  selectedModel: "deepseek-v3-0324",

  showChatPanel: false,
  replaceCalled: false,

  updateGit: false,
  elementSnapshot: null,
  pageSnapshot: null,
  undoStack: [],
  redoStack: [],
  undonePageSnapshot: false,
  undoneElementSnapshot: false,
  zoom: 1,
  apiCalled: false,
  isDemo: false,
  hideToolbar: false,
  viewingMode: "Controls",
  enableScaling: false,
  moveElements: true,
  chatVisible: true,
  chatExpanded: false,
  mediaItems: [],
  streamStatus: null,
  elementEditStatus: 'idle' as const,
  elementEditStreamHtml: '',
  elementEditOriginalHtml: '',


  setEnabledEditor: (id, enabled) =>
    set((state) => ({
      enabledEditor: {
        ...state.enabledEditor,
        [id]: enabled
      }
    })),
  // Actions
  setGitAPIAuthenticated: (authenticated) =>
    set((state) =>
      state.gitAPIAuthenticated !== authenticated
        ? { gitAPIAuthenticated: authenticated }
        : state
    ),

  setSidebarOption: (option) =>
    set((state) =>
      state.sidebarOption !== option ? { sidebarOption: option } : state
    ),

  setAction: (action) =>
    set((state) => (state.action !== action ? { action } : state)),

  setDevice: (device) =>
    set((state) => (state.device !== device ? { device } : state)),

  setEditMode: (editMode) =>
    set((state) => (state.editMode !== editMode ? { editMode } : state)),

  setPreviewTheme: (previewTheme) =>
    set((state) => (state.previewTheme !== previewTheme ? { previewTheme } : state)),

  setTitle: (title) =>
    set((state) => (state.title !== title ? { title } : state)),

  setState: (stateVal) =>
    set((state) => (state.state !== stateVal ? { state: stateVal } : state)),

  setTree: (tree) => set((state) => (state.tree !== tree ? { tree } : state)),

  setChatMessages: (chatMessages) =>
    set((state) =>
      state.chatMessages !== chatMessages ? { chatMessages } : state
    ),

  setSelectedFile: (file) =>
    set((state) =>
      state.selectedFile !== file ? { selectedFile: file } : state
    ),

  setTopPanelSize: (size) =>
    set((state) =>
      state.topPanelSize !== size ? { topPanelSize: size } : state
    ),

  setBottomPanelSize: (size) =>
    set((state) =>
      state.bottomPanelSize !== size ? { bottomPanelSize: size } : state
    ),

  setSidebarPanelSize: (size) =>
    set((state) =>
      state.sidebarPanelSize !== size ? { sidebarPanelSize: size } : state
    ),

  setEditorPanelSize: (size) =>
    set((state) =>
      state.editorPanelSize !== size ? { editorPanelSize: size } : state
    ),

  setCodePanelSize: (size) =>
    set((state) =>
      state.codePanelSize !== size ? { codePanelSize: size } : state
    ),

  setIframePanelSize: (size) =>
    set((state) =>
      state.iframePanelSize !== size ? { iframePanelSize: size } : state
    ),

  setPreviewPanelSize: (size) =>
    set((state) =>
      state.previewPanelSize !== size ? { previewPanelSize: size } : state
    ),

  setAIHubPanelSize: (size) =>
    set((state) =>
      state.aiHubPanelSize !== size ? { aiHubPanelSize: size } : state
    ),

  setSelectedElement: (elem) =>
    set((state) =>
      state.selectedElement !== elem ? { selectedElement: elem } : state
    ),

  setNewElementOuterHTML: (outerHTML) =>
    set((state) =>
      state.newElementOuterHTML !== outerHTML ? { newElementOuterHTML: outerHTML } : state
    ),
  setSelectedCode: (code) =>
    set((state) =>
      state.selectedCode !== code ? { selectedCode: code } : state
    ),

  setSelectedFileContent: (content) =>
    set((state) =>
      state.selectedFileContent !== content
        ? { selectedFileContent: content }
        : state
    ),
  setSrcDoc: (doc) =>
    set((state) => (state.srcDoc !== doc ? { srcDoc: doc } : state)),

  setProject: (project) =>
    set((state) => (state.project !== project ? { project } : state)),

  setProjectId: (id) =>
    set((state) => (state.projectId !== id ? { projectId: id } : state)),

  setRepoId: (id) =>
    set((state) => (state.repoId !== id ? { repoId: id } : state)),

  setLastGitCommitSha: (sha) =>
    set((state) =>
      state.lastGitCommitSha !== sha ? { lastGitCommitSha: sha } : state
    ),

  setEditorView: (view) =>
    set((state) => (state.editorView !== view ? { editorView: view } : state)),

  setIsIframeReady: (isReady) =>
    set((state) =>
      state.isIframeReady !== isReady ? { isIframeReady: isReady } : state
    ),

  setIsCodeEditorReady: (isReady) =>
    set((state) =>
      state.isCodeEditorReady !== isReady
        ? { isCodeEditorReady: isReady }
        : state
    ),

  setSelector: (selector) =>
    set((state) => (state.selector !== selector ? { selector } : state)),

  setCodeViewer: (viewer) =>
    set((state) =>
      state.codeViewer !== viewer ? { codeViewer: viewer } : state
    ),

  setScopeView: (view) =>
    set((state) => (state.scopeView !== view ? { scopeView: view } : state)),

  setScopeStyles: (styles) =>
    set((state) =>
      state.scopeStyles !== styles ? { scopeStyles: styles } : state
    ),

  setScopeScripts: (scripts) =>
    set((state) =>
      state.scopeScripts !== scripts ? { scopeScripts: scripts } : state
    ),

  setRepoName: (name) =>
    set((state) => (state.repoName !== name ? { repoName: name } : state)),

  setSelectedBranch: (branch) =>
    set((state) =>
      state.selectedBranch !== branch ? { selectedBranch: branch } : state
    ),

  setBranches: (list) =>
    set((state) =>
      JSON.stringify(state.branches) !== JSON.stringify(list)
        ? { branches: list }
        : state
    ),

  setRepoOwner: (owner) =>
    set((state) => (state.repoOwner !== owner ? { repoOwner: owner } : state)),
  setCodeView: (newView) =>
    set((state) =>
      state.codeView !== newView ? { codeView: newView } : state
    ),

  setChatMode: (mode) =>
    set((state) => (state.isChatMode !== mode ? { isChatMode: mode } : state)),

  setHasUndo: (hasUndo) =>
    set((state) => (state.hasUndo !== hasUndo ? { hasUndo: hasUndo } : state)),

  setHasRedo: (hasRedo) =>
    set((state) => (state.hasRedo !== hasRedo ? { hasRedo: hasRedo } : state)),

  setChangesCount: (count) =>
    set((state) =>
      state.changesCount !== count ? { changesCount: count } : state
    ),

  setHoveredElement: (element) =>
    set((state) =>
      state.hoveredElement !== element ? { hoveredElement: element } : state
    ),

  setChangesList: (list) =>
    set((state) =>
      JSON.stringify(state.changesList) !== JSON.stringify(list)
        ? { changesList: list }
        : state
    ),

  setCurrentChangeIndex: (index) =>
    set((state) =>
      state.currentChangeIndex !== index ? { currentChangeIndex: index } : state
    ),

  setCodeChange: (change) =>
    set((state) =>
      state.codeChange !== change ? { codeChange: change } : state
    ),

  setImportedStyles: (styles) =>
    set((state) =>
      state.importedStyles !== styles ? { importedStyles: styles } : state
    ),

  setImportedScripts: (scripts) =>
    set((state) =>
      state.importedScripts !== scripts ? { importedScripts: scripts } : state
    ),

  addPageSnapshot: (value) =>
    set((state) =>
      state.pageSnapshot !== value ? { pageSnapshot: value } : state
    ),

  addElementSnapshot: (value) =>
    set((state) =>
      state.elementSnapshot !== value ? { elementSnapshot: value } : state
    ),

  addChange: (newChange) =>
    set((state) => ({
      changesList: [
        ...state.changesList.slice(0, state.currentChangeIndex + 1),
        newChange,
      ],
      currentChangeIndex: state.currentChangeIndex + 1,
      changesCount: state.changesCount + 1,
    })),

  undoChange: () =>
    set((state) => ({
      currentChangeIndex: Math.max(state.currentChangeIndex - 1, -1),
      changesCount: Math.max(state.changesCount - 1, 0),
    })),

  redoChange: () =>
    set((state) => ({
      currentChangeIndex: Math.min(
        state.currentChangeIndex + 1,
        state.changesList.length - 1
      ),
      changesCount: state.changesCount + 1,
    })),

  saveChanges: () => {
    set((state) => {
      const changesToSave = state.changesList.slice(
        0,
        state.currentChangeIndex + 1
      );
      console.log("Saving changes:", changesToSave);

      // Handle saving logic here
      return { changesCount: 0 };
    });
  },

  setForceIframeRefresh: (refresh) =>
    set((state) =>
      state.forceIframeRefresh !== refresh
        ? { forceIframeRefresh: refresh }
        : state
    ),
  setCompanyName: (companyName) =>
    set((state) =>
      state.companyName !== companyName ? { companyName } : state
    ),

  setCompanyDescription: (companyDescription) =>
    set((state) =>
      state.companyDescription !== companyDescription
        ? { companyDescription }
        : state
    ),

  setLocation: (location) =>
    set((state) => (state.location !== location ? { location } : state)),

  setProjectSections: (projectSections) =>
    set((state) =>
      JSON.stringify(state.projectSections) !== JSON.stringify(projectSections)
        ? { projectSections }
        : state
    ),

  setProjectLayout: (projectLayout) =>
    set((state) =>
      state.projectLayout !== projectLayout ? { projectLayout } : state
    ),

  setProjectLayoutStyle: (projectLayoutStyle) =>
    set((state) =>
      state.projectLayoutStyle !== projectLayoutStyle
        ? { projectLayoutStyle }
        : state
    ),

  setProjectUIFramework: (projectUIFramework) =>
    set((state) =>
      state.projectUIFramework !== projectUIFramework
        ? { projectUIFramework }
        : state
    ),

  setProjectStyles: (projectStyles) =>
    set((state) =>
      JSON.stringify(state.projectStyles) !== JSON.stringify(projectStyles)
        ? { projectStyles }
        : state
    ),

  setProjectTexts: (projectTexts) =>
    set((state) =>
      JSON.stringify(state.projectTexts) !== JSON.stringify(projectTexts)
        ? { projectTexts }
        : state
    ),

  setProjectPrimaryColor: (color) =>
    set((state) =>
      state.projectPrimaryColor !== color
        ? { projectPrimaryColor: color }
        : state
    ),

  setProjectSecondaryColor: (color) =>
    set((state) =>
      state.projectSecondaryColor !== color
        ? { projectSecondaryColor: color }
        : state
    ),

  setProjectActiveStep: (step) =>
    set((state) =>
      state.projectActiveStep !== step ? { projectActiveStep: step } : state
    ),

  setProjectIndustry: (projectIndustry) =>
    set((state) =>
      state.projectIndustry !== projectIndustry ? { projectIndustry } : state
    ),

  setProjectOtherDetails: (projectOtherDetails) =>
    set((state) =>
      state.projectOtherDetails !== projectOtherDetails
        ? { projectOtherDetails }
        : state
    ),

  setProjectVision: (projectVision) =>
    set((state) =>
      state.projectVision !== projectVision ? { projectVision } : state
    ),

  setProjectMission: (projectMission) =>
    set((state) =>
      state.projectMission !== projectMission ? { projectMission } : state
    ),

  setProjectProducts: (projectProducts: string[]) =>
    set((state) => {
      const prev = state.projectProducts ?? [];
      const next = projectProducts ?? [];
      const same =
        prev.length === next.length &&
        prev.every((val, idx) => val === next[idx]);

      return same ? state : { projectProducts: next };
    }),

  setProjectAudience: (projectAudience) =>
    set((state) =>
      state.projectAudience !== projectAudience ? { projectAudience } : state
    ),

  setProjectGoal: (projectGoal) =>
    set((state) =>
      state.projectGoal !== projectGoal ? { projectGoal } : state
    ),

  setProjectImagesLinks: (projectImagesLinks) =>
    set((state) =>
      JSON.stringify(state.projectImagesLinks) !==
        JSON.stringify(projectImagesLinks)
        ? { projectImagesLinks }
        : state
    ),

  setProjectVideosLinks: (projectVideosLinks) =>
    set((state) =>
      JSON.stringify(state.projectVideosLinks) !==
        JSON.stringify(projectVideosLinks)
        ? { projectVideosLinks }
        : state
    ),

  setProjectPageName: (name) =>
    set((state) =>
      state.projectPageName !== name ? { projectPageName: name } : state
    ),

  setProjectLoading: (projectLoading) =>
    set((state) =>
      state.projectLoading !== projectLoading ? { projectLoading } : state
    ),

  setProjectTextsLength: (projectTextsLength) =>
    set((state) =>
      state.projectTextsLength !== projectTextsLength
        ? { projectTextsLength }
        : state
    ),

  setProjectTone: (projectTone) =>
    set((state) =>
      state.projectTone !== projectTone ? { projectTone } : state
    ),

  setProjectAddNewSection: (projectAddNewSection) =>
    set((state) =>
      state.projectAddNewSection !== projectAddNewSection
        ? { projectAddNewSection }
        : state
    ),

  setProjectWebsiteCode: (projectWebsiteCode) =>
    set((state) =>
      state.projectWebsiteCode !== projectWebsiteCode
        ? { projectWebsiteCode }
        : state
    ),

  setProjectTypography: (projectTypography) =>
    set((state) =>
      state.projectTypography !== projectTypography
        ? { projectTypography }
        : state
    ),

  setProjectLanguage: (projectLanguage) =>
    set((state) =>
      state.projectLanguage !== projectLanguage ? { projectLanguage } : state
    ),

  setIsAIWorking: (isAIWorking) =>
    set((state) =>
      state.isAIWorking !== isAIWorking ? { isAIWorking } : state
    ),

  setSelectedModel: (model) =>
    set((state) =>
      state.selectedModel !== model ? { selectedModel: model } : state
    ),
  setZoom: (zoom) => set((state) => (state.zoom !== zoom ? { zoom } : state)),



  setAPICalled: (called) =>
    set((state) =>
      state.apiCalled !== called ? { apiCalled: called } : state
    ),

  setIsDemo: (isDemo) =>
    set((state) => (state.isDemo !== isDemo ? { isDemo } : state)),

  setViewingMode: (mode) =>
    set((state) =>
      state.viewingMode !== mode ? { viewingMode: mode } : state
    ),

  setEnableScaling: (enable) =>
    set((state) =>
      state.enableScaling !== enable ? { enableScaling: enable } : state
    ),

  setMoveElements: (move) =>
    set((state) =>
      state.moveElements !== move ? { moveElements: move } : state
    ),

  setChatVisible: (value) =>
    set((state) =>
      state.chatVisible !== value ? { chatVisible: value } : state
    ),

  setChatExpanded: (value) =>
    set((state) =>
      state.chatExpanded !== value ? { chatExpanded: value } : state
    ),

  addMediaItem: (item) =>
    set((state) => ({
      mediaItems: [...state.mediaItems, item],
    })),

  removeMediaItem: (id) =>
    set((state) => ({
      mediaItems: state.mediaItems.filter((item) => item.id !== id),
    })),

  setMediaItems: (items) =>
    set((state) =>
      JSON.stringify(state.mediaItems) !== JSON.stringify(items)
        ? { mediaItems: items }
        : state
    ),
  setStreamStatus: (streamStatus) =>
    set((state) =>
      state.streamStatus !== streamStatus ? { streamStatus } : state
    ),

  resetStreamStatus: () => set({ streamStatus: null }),

  setUpdateGit: (update) =>
    set((state) =>
      state.updateGit !== update ? { updateGit: update } : state
    ),

  setNewBranchCreated: (created) =>
    set((state) =>
      state.newBranchCreated !== created ? { newBranchCreated: created } : state
    ),

  setShowChatPanel: (show) =>
    set((state) =>
      state.showChatPanel !== show ? { showChatPanel: show } : state
    ),

  setReplaceCalled: (replaced) =>
    set((state) =>
      state.replaceCalled !== replaced ? { replaceCalled: replaced } : state
    ),

  // Element edit streaming actions
  setElementEditStatus: (status) =>
    set((state) =>
      state.elementEditStatus !== status ? { elementEditStatus: status } : state
    ),

  appendElementEditStreamHtml: (token) =>
    set((state) => ({
      elementEditStreamHtml: state.elementEditStreamHtml + token,
    })),

  setElementEditOriginalHtml: (html) =>
    set({ elementEditOriginalHtml: html }),

  resetElementEditStream: () =>
    set({
      elementEditStatus: 'idle' as const,
      elementEditStreamHtml: '',
      elementEditOriginalHtml: '',
    }),
}));

export default useEditorStore;
